import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { School, Mail, Lock, Eye, EyeOff, AlertCircle, RefreshCw } from 'lucide-react';
import { clearAISchoolCache, clearAuthCache } from '../utils/cacheUtils';
import './Auth.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false);
  const [cacheStatus, setCacheStatus] = useState('');
  const [redirectStatus, setRedirectStatus] = useState('');

  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Limpar cache automaticamente ao carregar a página
  useEffect(() => {
    const clearCache = async () => {
      try {
        console.log('🧹 Iniciando limpeza automática de cache...');
        
        // Usar a função utilitária para limpeza completa
        const result = await clearAISchoolCache(false);
        
        if (result.success) {
          setCacheCleared(true);
          setCacheStatus(`Cache limpo: ${result.results.localStorage} localStorage, ${result.results.sessionStorage} sessionStorage, ${result.results.browserCache} browser cache`);
          console.log('✅ Cache limpo com sucesso!', result.results);
          
          // Mostrar mensagem de sucesso temporariamente
          setTimeout(() => {
            setCacheCleared(false);
            setCacheStatus('');
          }, 5000);
        } else {
          console.warn('⚠️ Cache não pôde ser limpo completamente:', result.message);
        }
        
      } catch (error) {
        console.error('❌ Erro ao limpar cache:', error);
        // Mesmo com erro, continuar com o login
      }
    };

    // Executar limpeza de cache
    clearCache();
  }, []);

  // Detectar quando usuário foi autenticado e redirecionar
  useEffect(() => {
    if (user && !authLoading && user.userType) {
      console.log('🔄 User authenticated, checking redirection...', {
        email: user.email,
        userType: user.userType,
        id: user.id
      });
      
      // Aguardar um pouco para garantir que o perfil foi carregado completamente
      const redirectTimer = setTimeout(() => {
        // Redirecionar baseado no tipo de usuário
        // Na base: 'student' = aluno, 'admin' = administrador
        if (user.userType === 'student') {
          console.log('🎓 Redirecting student to /aluno');
          setRedirectStatus('🎓 Redirecionando para área do aluno...');
          navigate('/aluno', { replace: true });
        } else {
          console.log('👨‍💼 Redirecting admin to dashboard');
          setRedirectStatus('👨‍💼 Redirecionando para dashboard...');
          const from = location.state?.from?.pathname || '/';
          navigate(from, { replace: true });
        }
      }, 1000); // Delay aumentado para garantir carregamento completo
      
      return () => clearTimeout(redirectTimer);
    }
  }, [user, authLoading, navigate, location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.email || !formData.password) {
      setError('Por favor, preencha todos os campos');
      setLoading(false);
      return;
    }

    console.log('Submitting login form for:', formData.email);
    const result = await login(formData.email, formData.password);
    
    console.log('Login result:', result);
    
    if (result.success) {
      console.log('Login successful, AuthContext will handle redirection');
      // O AuthContext vai detectar o login e fazer o redirecionamento automaticamente
      // Não fazemos redirecionamento manual aqui para evitar conflitos
    } else {
      console.error('Login failed:', result.error);
      setError(result.error);
      setLoading(false);
    }
  };

  // Função para limpar cache manualmente
  const handleManualCacheClear = async () => {
    setLoading(true);
    try {
      console.log('🧹 Limpeza manual de cache iniciada...');
      
      // Usar a função utilitária para limpeza completa com reload
      const result = await clearAISchoolCache(true);
      
      if (result.success) {
        console.log('✅ Cache limpo manualmente:', result.message);
        // A função já vai recarregar a página
      } else {
        setError('Erro ao limpar cache: ' + result.message);
        setLoading(false);
      }
      
    } catch (error) {
      console.error('❌ Erro na limpeza manual:', error);
      setError('Erro ao limpar cache. Tente recarregar a página.');
      setLoading(false);
    }
  };

  // Função para limpar apenas cache de autenticação
  const handleAuthCacheClear = async () => {
    try {
      console.log('🔐 Limpando cache de autenticação...');
      
      const result = await clearAuthCache();
      
      if (result.success) {
        setCacheStatus('Cache de autenticação limpo!');
        setTimeout(() => setCacheStatus(''), 3000);
      } else {
        setError('Erro ao limpar cache de autenticação');
      }
      
    } catch (error) {
      console.error('❌ Erro ao limpar cache de auth:', error);
      setError('Erro ao limpar cache de autenticação');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/logo.png" alt="INDEC Evolua" className="auth-logo-image" />
          </div>
          <h2>Entrar na sua conta</h2>
          <p>Acesse o painel administrativo</p>
          
          {/* Indicadores de cache removidos - funcionalidade mantida em background */}
          
          {/* Status de redirecionamento */}
          {redirectStatus && (
            <div className="auth-success">
              <RefreshCw size={16} />
              <span>{redirectStatus}</span>
            </div>
          )}
        </div>

        {error && (
          <div className="auth-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="seu@email.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <div className="input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Sua senha"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        {/* Controles de cache removidos - funcionalidade de limpeza automática mantida */}

        {/* Botão de criar conta comentado - pode ser usado futuramente
        <div className="auth-footer">
          <p>
            Não tem uma conta?{' '}
            <Link to="/register" className="auth-link">
              Criar conta
            </Link>
          </p>
        </div>
        */}
      </div>
    </div>
  );
};

export default Login;
